import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Search, Trash2, ChevronRight, ChevronLeft, Loader2, RotateCcw } from "lucide-react";
import { api, faDigits, faNum, fmtDate } from "../lib/api";

const STATUS_COLORS = {
  found: "bg-emerald-50 text-emerald-700 border-emerald-200",
  not_matched: "bg-amber-50 text-amber-700 border-amber-200",
  cookie_invalid: "bg-rose-50 text-rose-700 border-rose-200",
  timeout: "bg-rose-50 text-rose-700 border-rose-200",
  connection_error: "bg-rose-50 text-rose-700 border-rose-200",
  service_unavailable: "bg-slate-100 text-slate-700 border-slate-200",
  upstream_error: "bg-rose-50 text-rose-700 border-rose-200",
  invalid_response: "bg-rose-50 text-rose-700 border-rose-200",
  matched: "bg-emerald-50 text-emerald-700 border-emerald-200",
  sana_in: "bg-emerald-50 text-emerald-700 border-emerald-200",
  sana_not_in: "bg-amber-50 text-amber-700 border-amber-200",
  mobile_mismatch: "bg-amber-50 text-amber-700 border-amber-200",
  cookie_expired: "bg-rose-50 text-rose-700 border-rose-200",
  unavailable: "bg-slate-100 text-slate-700 border-slate-200",
};

const SOURCE_LABEL = {
  cache: "کش داخلی",
  upstream: "عدلیران",
  online: "عدلیران آنلاین",
  sana_membership: "ثنا (عضویت)",
  sana_shahkar: "ثنا (شاهکار)",
  shahkar: "شاهکار (پروایدر)",
};

export default function Inquiries() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [status, setStatus] = useState("");
  const [source, setSource] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [keepModal, setKeepModal] = useState(false);
  const [keepN, setKeepN] = useState(50);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/logs", {
        params: { page, page_size: pageSize, status, source, search },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch { toast.error("خطا"); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, status, source]);
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const deleteAll = async () => {
    if (!window.confirm("همه لاگ‌ها حذف شوند؟")) return;
    await api.delete("/admin/logs");
    toast.success("لاگ‌ها پاک شدند");
    load();
  };

  const keepLast = async () => {
    await api.post("/admin/logs/keep-last", { n: parseInt(keepN) });
    toast.success(`تنها ${faDigits(keepN)} رکورد آخر نگه داشته شد`);
    setKeepModal(false);
    load();
  };

  return (
    <div data-testid="inquiries-page" className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">استعلامات اخیر</h1>
          <p className="text-sm text-slate-500 mt-1">مجموع: <b className="tabular-nums">{faNum(total)}</b> رکورد</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="inquiries-keep-last-button" onClick={() => setKeepModal(true)} className="bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 px-4 py-2.5 rounded-xl flex items-center gap-2"><RotateCcw size={16} /> نگهداری N رکورد آخر</button>
          <button data-testid="inquiries-delete-all-button" onClick={deleteAll} className="bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 px-4 py-2.5 rounded-xl flex items-center gap-2"><Trash2 size={16} /> حذف همه</button>
        </div>
      </div>

      <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm p-4 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input data-testid="inquiries-search-input" placeholder="جستجو کد ملی" value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pr-10 pl-4 py-2.5 rounded-xl border border-slate-300 focus:border-teal-500 outline-none" />
        </div>
        <select data-testid="inquiries-status-filter" value={status} onChange={e => setStatus(e.target.value)} className="px-4 py-2.5 rounded-xl border border-slate-300 bg-white">
          <option value="">همه وضعیت‌ها</option>
          <option value="found">موفق (found)</option>
          <option value="not_matched">عدم تطابق</option>
          <option value="cookie_invalid">کوکی نامعتبر</option>
          <option value="timeout">تایم‌اوت</option>
          <option value="upstream_error">خطای سرویس‌دهنده</option>
          <option value="service_unavailable">در دسترس نیست</option>
        </select>
        <select data-testid="inquiries-source-filter" value={source} onChange={e => setSource(e.target.value)} className="px-4 py-2.5 rounded-xl border border-slate-300 bg-white">
          <option value="">همه منابع</option>
          <option value="upstream">عدلیران</option>
          <option value="online">عدلیران آنلاین</option>
          <option value="cache">کش داخلی</option>
          <option value="sana_membership">ثنا (عضویت)</option>
          <option value="sana_shahkar">ثنا (شاهکار)</option>
          <option value="shahkar">شاهکار (پروایدر)</option>
        </select>
      </div>

      <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-right text-sm">
            <thead className="bg-slate-50 text-slate-600 font-medium">
              <tr>
                <th className="py-3 px-4 border-b">زمان</th>
                <th className="py-3 px-4 border-b">کد ملی</th>
                <th className="py-3 px-4 border-b">تاریخ تولد</th>
                <th className="py-3 px-4 border-b">موبایل</th>
                <th className="py-3 px-4 border-b">کش</th>
                <th className="py-3 px-4 border-b">وضعیت</th>
                <th className="py-3 px-4 border-b">منبع</th>
                <th className="py-3 px-4 border-b">مدت</th>
                <th className="py-3 px-4 border-b">اکانت</th>
                <th className="py-3 px-4 border-b">خطا</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={10} className="p-8 text-center"><Loader2 className="animate-spin inline" /></td></tr>
                : items.length === 0 ? <tr><td colSpan={10} className="p-8 text-center text-slate-400">هیچ رکوردی یافت نشد</td></tr>
                : items.map((r, i) => (
                <motion.tr key={r.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.015 }} className="row-hover border-b border-slate-100 last:border-0">
                  <td className="py-3 px-4 text-xs">{fmtDate(r.ts)}</td>
                  <td className="py-3 px-4 tabular-nums">{faDigits(r.national_code)}</td>
                  <td className="py-3 px-4 tabular-nums text-xs">{faDigits(r.birth_date)}</td>
                  <td className="py-3 px-4 tabular-nums text-xs">{r.mobile ? faDigits(r.mobile) : "-"}</td>
                  <td className="py-3 px-4 text-xs">
                    {r.cache_action === "saved" ? (
                      <span className="px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">ذخیره شد</span>
                    ) : r.cache_action === "already_cached" ? (
                      <span className="px-2 py-0.5 rounded-full border bg-slate-100 text-slate-700 border-slate-200">از قبل در کش بود</span>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>
                  <td className="py-3 px-4"><span className={`text-xs px-2 py-1 rounded-full border ${STATUS_COLORS[r.status] || "bg-slate-100 border-slate-200"}`}>{r.status}</span></td>
                  <td className="py-3 px-4 text-xs"><span className={`px-2 py-0.5 rounded ${r.source === "cache" ? "bg-indigo-50 text-indigo-700" : r.source?.startsWith("sana") ? "bg-violet-50 text-violet-700" : "bg-teal-50 text-teal-700"}`}>{SOURCE_LABEL[r.source] || r.source || "عدلیران"}</span></td>
                  <td className="py-3 px-4 tabular-nums text-xs">{faNum(r.duration_ms)} ms</td>
                  <td className="py-3 px-4 font-mono text-xs">{r.account_used || "-"}</td>
                  <td className="py-3 px-4 text-xs text-rose-600 max-w-xs truncate" title={r.error_message}>{r.error_message || "-"}</td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={totalPages} onPage={setPage} testidPrefix="inquiries" />
      </div>

      {keepModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm grid place-items-center z-50 p-4" onClick={() => setKeepModal(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()} data-testid="keep-last-modal">
            <h3 className="text-lg font-semibold mb-3">نگه داشتن N رکورد آخر</h3>
            <p className="text-sm text-slate-500 mb-4">فقط N رکورد اخیر باقی می‌ماند، بقیه حذف می‌شوند.</p>
            <input type="number" min={1} value={keepN} onChange={e => setKeepN(e.target.value)} className="w-full px-4 py-2 rounded-xl border border-slate-300 mb-4" data-testid="keep-n-input" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setKeepModal(false)} className="px-4 py-2 rounded-xl bg-slate-100">انصراف</button>
              <button data-testid="keep-last-confirm" onClick={keepLast} className="px-4 py-2 rounded-xl bg-teal-600 text-white">اعمال</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function Pagination({ page, totalPages, onPage, testidPrefix = "pg" }) {
  return (
    <div className="flex items-center justify-between p-4 border-t border-slate-100">
      <span className="text-sm text-slate-500">صفحه <b className="tabular-nums">{faDigits(page)}</b> از <b className="tabular-nums">{faDigits(totalPages)}</b></span>
      <div className="flex gap-1">
        <button data-testid={`${testidPrefix}-page-prev`} onClick={() => onPage(Math.max(1, page - 1))} disabled={page <= 1} className="px-3 py-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-40 flex items-center gap-1"><ChevronRight size={16} /> قبلی</button>
        <button data-testid={`${testidPrefix}-page-next`} onClick={() => onPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages} className="px-3 py-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-40 flex items-center gap-1">بعدی <ChevronLeft size={16} /></button>
      </div>
    </div>
  );
}
